// Business side: connect the business's own Stripe account so clients can pay
// invoices by card in the portal. Money goes to the business's Stripe account.
import React, { useCallback, useEffect, useState } from 'react';
import clientPortalAPI from './clientPortalApi';
import { formatApiError } from '../../utils/errorMessage';

const LABEL = { not_connected: 'Not connected', pending: 'Pending: Stripe needs more details', active: 'Active: clients can pay by card' };

export default function StripeConnectCard({ businessId }) {
  const [state, setState] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');

  const load = useCallback(async () => {
    try {
      const back = new URLSearchParams(window.location.search).get('stripe');
      setState((back ? await clientPortalAPI.stripeRefresh(businessId) : await clientPortalAPI.stripeStatus(businessId)).data);
    } catch (e) { setError(formatApiError(e, 'Could not load the Stripe status')); }
  }, [businessId]);
  useEffect(() => { load(); }, [load]);

  const onboard = async () => {
    setBusy(true); setError('');
    try { const r = await clientPortalAPI.stripeOnboard(businessId); window.location.assign(r.data.url); } catch (e) { setError(formatApiError(e, 'Could not open Stripe')); setBusy(false); }
  };

  // Sandbox only: the server reports testOnboardingAvailable just when the
  // test-complete route would accept the call.
  const completeTest = async () => {
    setBusy(true); setError(''); setNotice('');
    try {
      const r = await clientPortalAPI.stripeTestComplete(businessId);
      const account = (r.data && r.data.account) || {};
      const fresh = (await clientPortalAPI.stripeStatus(businessId)).data;
      setState(fresh);
      const status = fresh.status || account.status;
      setNotice(status === 'active' ? 'Test onboarding finished. Status: Active.' : `Test onboarding sent. Stripe status: ${LABEL[status] || status}.`);
    } catch (e) { setError(formatApiError(e, 'Stripe could not finish test onboarding')); }
    setBusy(false);
  };

  if (!state) return error ? <p role="alert" className="text-red-600 text-xs">{error}</p> : null;
  return (
    <div className="border rounded p-2 text-sm space-y-1" aria-label="Card payments">
      <p className="font-medium">Card payments (Stripe)</p>
      <p data-testid="stripe-status">Status: {LABEL[state.status] || state.status}{state.test_mode ? ' (test mode)' : ''}</p>
      {state.configured === false ? <p className="text-gray-600">Card payments are not available yet.</p> : (
        <>
          {state.status !== 'active' && (
            <ol className="list-decimal ml-5 text-gray-700">
              <li>Click Connect Stripe. You go to Stripe&apos;s secure page.</li>
              <li>Enter your business and bank details there.</li>
              <li>Come back here. When the status is Active, clients see a Pay button on open invoices.</li>
            </ol>
          )}
          <div className="flex gap-3">
            <button type="button" disabled={busy} onClick={onboard} className="border rounded px-3 py-1">{state.status === 'not_connected' ? 'Connect Stripe' : 'Update Stripe details'}</button>
            {state.status !== 'not_connected' && <button type="button" disabled={busy} className="text-blue-600 text-xs" onClick={() => clientPortalAPI.stripeRefresh(businessId).then((r) => setState({ ...state, ...r.data })).catch((e) => setError(formatApiError(e, 'Could not check Stripe')))}>Check status</button>}
            {state.testOnboardingAvailable === true && <button type="button" disabled={busy} onClick={completeTest} className="border rounded px-3 py-1">Complete test onboarding</button>}
          </div>
        </>
      )}
      {notice && <p role="status" className="text-green-700 text-xs">{notice}</p>}
      {error && <p role="alert" className="text-red-600 text-xs">{error}</p>}
    </div>
  );
}
